import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { attributionRef } from "@/lib/attribution";
import {
  contact as fallbackContact,
  highlights as fallbackHighlights,
  services as fallbackServices,
  coverage as fallbackCoverage,
  clientPortalUrl,
} from "@/content/site";

export type BrandContent = {
  about: string;
  phoneDisplay: string;
  email: string;
  whatsapp: string;
  clientPortalUrl: string;
  /** Caminho no storage (ou URL) do logo. Vazio = logo padrão. */
  logoUrl: string;
  /** Imagem padrão do hero quando o banner não tem imagem própria. */
  heroImage: string;
};

export type HighlightItem = { value: string; label: string; icon: string };

export type TestimonialItem = { name: string; role: string; text: string };
export type TestimonialsContent = {
  eyebrow: string;
  title: string;
  items: TestimonialItem[];
};
export type ServiceItem = {
  icon: string;
  tag: string;
  title: string;
  text: string;
  ctaLabel?: string;
  ctaHref?: string;
};

export type ServicesContent = { title: string; items: ServiceItem[] };

export type CoverageContent = {
  ctaLabel: string;
  /** Frase ao lado do botão, na faixa de "consulte a cobertura". */
  ctaTitle: string;
};

export type FooterLink = { label: string; to: string };
export type FooterContent = {
  supportNote: string;
  columns: { title: string; links: FooterLink[] }[];
};

export type PartnerItem = { name: string; image: string };
export type PartnersContent = {
  title: string;
  subtitle: string;
  /** Segundos por volta da faixa de logos. Maior = mais devagar. */
  speed?: string;
  /** Tamanho das logos na faixa: "sm" | "md" | "lg". */
  logoSize?: string;
  items: PartnerItem[];
};

export type PhonePlanItem = {
  name: string;
  plan: string;
  price: string;
  note: string;
  badge?: string;
  ctaLabel?: string;
  features?: string[];
};
export type PhoneContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  /** Parágrafo de apresentação abaixo do topo da página. */
  text: string;
  items: PhonePlanItem[];
};

export type HostingPlanItem = {
  name: string;
  price: string;
  disk: string;
  transfer: string;
  domains: string;
  emails: string;
  sending: string;
  platform: string;
  /** Tabela "você poderá criar" */
  cpanel?: string;
  subdomains?: string;
  extraDomains?: string;
  pop3?: string;
  databases?: string;
  ftp?: string;
  badge?: string;
};
export type HostingContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  /** Parágrafo de apresentação abaixo do topo da página. */
  text: string;
  items: HostingPlanItem[];
  panelTitle: string;
  /** Um recurso por linha. */
  panelFeatures: string;
  emailTitle: string;
  /** Um recurso por linha. */
  emailFeatures: string;
  ctaLabel: string;
};

export type AppLogo = {
  name: string;
  image: string;
  /** Usado só na página de Aplicativos, pra separar as duas caixas de ícones. */
  tier?: "standard" | "premium";
};

export type PerkItem = { title: string; text: string };

export type InternetContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  /** Parágrafo de apresentação abaixo do topo da página. */
  text: string;
  ctaLabel: string;
  /** Nota pequena logo abaixo dos cards de plano (ex.: condição da instalação grátis). */
  disclaimer: string;
  /** Logos dos apps parceiros exibidos nos cards de plano. */
  apps: AppLogo[];
  bannerTitle: string;
  bannerText: string;
  bannerImage: string;
  bannerCtaLabel: string;
  bannerCtaHref: string;
  /** Seção "O que pode acompanhar seu plano", abaixo dos cards. */
  perksEyebrow: string;
  perksTitle: string;
  perksItems: PerkItem[];
};

export type SimplePlanItem = {
  name: string;
  price: string;
  note: string;
  badge?: string;
  ctaLabel?: string;
  features?: string[];
  /** Só usado nos combos de Aplicativos, pra ligar o card à caixa de apps certa. */
  tier?: "standard" | "premium";
};

export type CamerasContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  text: string;
  items: SimplePlanItem[];
};

export type AppsContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  text: string;
  items: SimplePlanItem[];
  apps: AppLogo[];
};

export type BlogPost = {
  title: string;
  date: string;
  excerpt: string;
  image: string;
  /** Endereço do post no site (ex.: minha-noticia). Gerado do título se vazio. */
  slug?: string;
  /** Texto completo do post (HTML ou texto com parágrafos). */
  body?: string;
  /** Categoria do post (ex.: Tecnologia, Streaming, Dicas de Wi-Fi, Novidades). */
  category?: string;
  /** Nome do autor ou departamento (ex.: Equipe Contato, Redação). */
  author?: string;
  /** Se o post está em destaque no topo da página do blog. */
  featured?: boolean;
  /** Tempo estimado de leitura em minutos (opcional, calculado automaticamente se omitido). */
  readTime?: string;
};

export type BlogContent = {
  title: string;
  subtitle: string;
  items: BlogPost[];
};

export type JobItem = { role: string; location: string; url: string };
export type JobsContent = {
  title: string;
  subtitle: string;
  items: JobItem[];
  talentTitle: string;
  talentText: string;
  talentCtaLabel: string;
  talentCtaHref: string;
};

export type StoreItem = {
  name: string;
  address: string;
  district: string;
  zip: string;
  mapUrl: string;
};
export type StoresContent = {
  title: string;
  subtitle: string;
  items: StoreItem[];
};

export type AboutContent = {
  title: string;
  /** Parágrafos separados por linha em branco. */
  text: string;
  /** Foto da empresa/equipe, ao lado dos textos. Caminho no storage ou data URI. */
  image: string;
  missionTitle: string;
  mission: string;
  visionTitle: string;
  vision: string;
  valuesTitle: string;
  /** Um valor por linha. */
  values: string;
};

export type PromoContent = {
  enabled: boolean;
  badge: string;
  title: string;
  text: string;
  image: string;
  ctaLabel: string;
  ctaHref: string;
  /** Tamanho da janela do popup, no mobile e no desktop. */
  size?: "sm" | "md" | "lg";
};

export type IntegrationsContent = {
  /** Os 3 valores vêm do código de instalação em Huggy → Configurações → Canais → Widget. */
  huggyWidgetId: string;
  huggyCompany: string;
  huggyContextId: string;
  dataCenterUrl: string;
  webmailUrl: string;
};

export type SiteContent = {
  brand: BrandContent;
  highlights: HighlightItem[];
  testimonials: TestimonialsContent;
  services: ServicesContent;
  coverage: CoverageContent;
  partners: PartnersContent;
  internet: InternetContent;
  phone: PhoneContent;
  hosting: HostingContent;
  cameras: CamerasContent;
  apps: AppsContent;
  blog: BlogContent;
  jobs: JobsContent;
  stores: StoresContent;
  about: AboutContent;
  promo: PromoContent;
  integrations: IntegrationsContent;
  footer: FooterContent;
};

export const contentDefaults: SiteContent = {
  brand: {
    about:
      "Provedor de internet via fibra óptica e rádio, telefonia e hospedagem para residências e empresas.",
    phoneDisplay: fallbackContact.phoneDisplay,
    email: fallbackContact.email,
    whatsapp: fallbackContact.whatsapp,
    clientPortalUrl,
    logoUrl: "",
    heroImage: "",
  },
  highlights: fallbackHighlights.map((h) => ({ ...h })),
  testimonials: {
    eyebrow: "Depoimentos",
    title: "O que dizem nossos clientes",
    items: [
      {
        name: "Kauan Rafael",
        role: "Cliente residencial",
        text: "Trocamos de operadora e a diferença foi imediata. A internet não cai e o suporte responde na hora.",
      },
      {
        name: "Renan Fernandes",
        role: "Cliente empresarial",
        text: "Nossa operação depende de conexão estável. Desde que migramos para a Contato não tivemos parada.",
      },
    ],
  },
  services: { title: "Nossos serviços", items: fallbackServices.map((s) => ({ ...s })) },
  coverage: {
    ctaLabel: fallbackCoverage.ctaLabel,
    ctaTitle: fallbackCoverage.ctaTitle,
  },
  partners: {
    title: "+1000 empresas confiam na Contato",
    subtitle:
      "Conectividade, telefonia e cloud para negócios de todos os tamanhos na nossa região.",
    speed: "32",
    logoSize: "md",
    items: [
      { name: "Supermercados Rede Sul", image: "" },
      { name: "Clínica Vida", image: "" },
      { name: "Construtora Horizonte", image: "" },
      { name: "Auto Peças Litoral", image: "" },
      { name: "Colégio Progresso", image: "" },
      { name: "Hotel Mar Azul", image: "" },
      { name: "Transportes Araranguá", image: "" },
      { name: "Padaria Pão Nosso", image: "" },
    ],
  },

  phone: {
    eyebrow: "Contato Telefonia",
    title: "Planos de telefonia Fale Mais",
    subtitle: "Fale à vontade com a telefonia fixa da Contato.",
    text: "Os planos Fale Mais oferecem ligações ilimitadas, portabilidade gratuita do seu número atual e recursos como identificador de chamadas, caixa postal e siga-me. Tudo instalado pela nossa equipe e com o suporte local de sempre, sem fidelidade escondida.",
    items: [
      {
        name: "Fale Mais",
        plan: "Local",
        price: "R$ 29,90 / mês",
        note: "Combo com internet",
        ctaLabel: "Contratar",
        features: [
          "Ligação fixo local - Ilimitado",
          "Ligação Fixo LDN - 50 minutos",
          "Ligação Celular Brasil - 50 minutos",
        ],
      },
      {
        name: "Fale Mais",
        plan: "Plus",
        price: "R$ 49,90 / mês",
        note: "Combo com internet",
        badge: "Mais vendido",
        ctaLabel: "Contratar",
        features: [
          "Ligação fixo local - Ilimitado",
          "Ligação Fixo LDN - 100 minutos",
          "Ligação Celular Brasil - 100 minutos",
        ],
      },
      {
        name: "Fale Mais",
        plan: "Plus 250",
        price: "R$ 119,90/ mês",
        note: "Combo",
        ctaLabel: "Contratar",
        features: [
          "Ligação fixo local - Ilimitado",
          "Ligação Fixo LDN - 250 minutos",
          "Ligação Celular Brasil - 250 minutos",
        ],
      },
      {
        name: "Fale Mais",
        plan: "Plus 500",
        price: "R$ 239,90/ mês",
        note: "Combo",
        ctaLabel: "Contratar",
        features: [
          "Ligação fixo local - Ilimitado",
          "Ligação Fixo LDN - 500 minutos",
          "Ligação Celular Brasil - 500 minutos",
        ],
      },
      {
        name: "Fale Mais",
        plan: "Plus 1000",
        price: "R$ 499,90/ mês",
        note: "Combo",
        ctaLabel: "Contratar",
        features: [
          "Ligação fixo local - Ilimitado",
          "Ligação Fixo LDN - 1000 minutos",
          "Ligação Celular Brasil - 1000 minutos",
        ],
      },
      {
        name: "Fale Mais",
        plan: "Plus 2000",
        price: "R$ 999,90/ mês",
        note: "Combo",
        ctaLabel: "Contratar",
        features: [
          "Ligação fixo local - Ilimitado",
          "Ligação Fixo LDN - 2000 minutos",
          "Ligação Celular Brasil - 2000 minutos",
        ],
      },
    ],
  },

  hosting: {
    eyebrow: "Contato Hospedagem",
    title: "Planos de hospedagem de sites",
    subtitle:
      "Profissionalismo que o seu negócio merece: servidores Linux, cPanel, e-mails profissionais e suporte de gente de verdade.",
    text: "Hospede o site da sua empresa em servidores estáveis, com painel cPanel, contas de e-mail profissionais no seu domínio, banco de dados e backups. Planos para todos os tamanhos de projeto e uma equipe local pronta para ajudar na migração e no dia a dia.",
    items: [
      {
        name: "Compact",
        price: "9,90",
        disk: "500 MB",
        transfer: "5 GB",
        domains: "1",
        emails: "10",
        sending: "120/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "não",
        extraDomains: "não",
        pop3: "5",
        databases: "1",
        ftp: "1",
      },
      {
        name: "Basic",
        price: "14,90",
        disk: "1 GB",
        transfer: "10 GB",
        domains: "2",
        emails: "20",
        sending: "120/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "2",
        extraDomains: "2",
        pop3: "20",
        databases: "2",
        ftp: "2",
      },
      {
        name: "Advanced",
        price: "19,90",
        disk: "1.5 GB",
        transfer: "25 GB",
        domains: "5",
        emails: "30",
        sending: "120/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "5",
        extraDomains: "5",
        pop3: "30",
        databases: "5",
        ftp: "5",
      },
      {
        name: "Professional",
        price: "29,90",
        disk: "2 GB",
        transfer: "35 GB",
        domains: "5",
        emails: "40",
        sending: "150/h",
        platform: "Linux",
        badge: "Mais vendido",
        cpanel: "1",
        subdomains: "5",
        extraDomains: "5",
        pop3: "30",
        databases: "5",
        ftp: "5",
      },
      {
        name: "Company",
        price: "39,90",
        disk: "2.5 GB",
        transfer: "100 GB",
        domains: "10",
        emails: "50",
        sending: "175/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "10",
        extraDomains: "10",
        pop3: "50",
        databases: "10",
        ftp: "10",
      },
      {
        name: "Enterprise",
        price: "49,90",
        disk: "5 GB",
        transfer: "200 GB",
        domains: "10",
        emails: "100",
        sending: "200/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "10",
        extraDomains: "10",
        pop3: "100",
        databases: "10",
        ftp: "10",
      },
      {
        name: "Corporate",
        price: "119,90",
        disk: "15 GB",
        transfer: "400 GB",
        domains: "15",
        emails: "Ilimitado",
        sending: "220/h",
        platform: "Linux",
        cpanel: "1",
        subdomains: "15",
        extraDomains: "15",
        pop3: "ilimitado",
        databases: "15",
        ftp: "15",
      },
    ],
    panelTitle: "Nosso painel de controle possui",
    panelFeatures: [
      "PHP MyAdmin",
      "Bloqueio de IPs",
      "Página de erros customizáveis",
      "Redirecionador de URL",
      "Gerenciador de arquivos via web",
      "Proteção de diretórios com senha",
      "Blogs",
      "PHP Nuke",
      "Fóruns",
      "Contadores",
      "Livro de visitas",
    ].join("\n"),
    emailTitle: "Componentes de e-mail",
    emailFeatures: [
      "Webmail",
      "Alias de e-mail",
      "Responder automático",
      "Lista de e-mail",
      "E-mail pega tudo",
      "Antispam (Spam Assassin)",
      "Redirecionador",
      "IMAP / POP3",
      "SMTP autenticado",
    ].join("\n"),
    ctaLabel: "Contratar",
  },

  internet: {
    eyebrow: "Contato Internet",
    title: "Planos de internet fibra óptica",
    subtitle:
      "Fibra 100% óptica com Wi-Fi incluso, instalação grátis e suporte de gente de verdade. Escolha a velocidade ideal para a sua casa e ainda leve apps de streaming e música inclusos no plano.",
    text: "A Contato Internet é um dos maiores provedores de internet de Santa Catarina — e sabe exatamente o que nos move: conectar pessoas, empresas e famílias a um mundo de possibilidades.\n\nCom uma infraestrutura de fibra óptica de alta performance, entregamos muito mais do que velocidade: entregamos estabilidade, baixa latência e uma experiência de conexão que acompanha o seu ritmo de vida.\n\nPor que escolher a fibra óptica da Contato?\n\nVelocidade real\nPlanos pensados para o seu consumo, com download e upload de altíssima velocidade para streaming, games, home office e estudos sem travamentos.\n\nEstabilidade que você pode confiar\nA fibra óptica é imune a interferências elétricas e climáticas, garantindo uma conexão firme e constante em qualquer hora do dia.\n\nSuporte humano de verdade\nNosso compromisso vai além da conexão. Contamos com uma equipe de atendimento pronta para ajudar sempre que você precisar — com agilidade, respeito e atenção ao cliente.\n\nCobertura e proximidade\nComo provedor regional, entendemos as necessidades da nossa comunidade e estamos presentes de perto, do começo ao fim da instalação e no dia a dia do uso.\n\nMais que internet, um compromisso\nEscolher a Contato Internet é escolher um parceiro que investe constantemente em tecnologia, que valoriza cada cliente e que trabalha todos os dias para manter Santa Catarina cada vez mais conectada.\n\nVenha para a fibra óptica da Contato e sinta a diferença de navegar sem limites.\n\n*Instalação gratuita mediante análise de crédito e viabilidade técnica",
    ctaLabel: "Assine agora",
    disclaimer: "*Instalação gratuita mediante análise de crédito e viabilidade técnica.",
    apps: [
      { name: "Deezer", image: "/apps/deezer.svg" },
      { name: "Max", image: "/apps/max.svg" },
      { name: "Looke", image: "/apps/looke.svg" },
      { name: "Paramount+", image: "/apps/paramount-plus.svg" },
    ],
    bannerTitle: "Indique a Contato e ganhe desconto",
    bannerText:
      "Indique um amigo, ele assina a fibra da Contato e vocês dois ganham desconto na mensalidade.",
    bannerImage: "",
    bannerCtaLabel: "Quero indicar",
    bannerCtaHref: "whatsapp:Olá! Quero indicar um amigo para a Contato.",
    perksEyebrow: "O que pode acompanhar seu plano",
    perksTitle: "Você escolhe a internet, a Contato completa a experiência",
    perksItems: [
      {
        title: "Instalação grátis",
        text: "Equipe técnica instala e configura tudo, sem custo adicional na maioria dos planos.",
      },
      {
        title: "Wi-Fi incluso",
        text: "Roteador de alta performance para levar o sinal forte por toda a casa.",
      },
      {
        title: "TV Online",
        text: "Canais ao vivo e apps de entretenimento disponíveis como complemento do seu plano.",
      },
      {
        title: "Apps de música",
        text: "Deezer e outros serviços podem ser adicionados conforme a oferta vigente.",
      },
    ],
  },

  cameras: {
    eyebrow: "Contato Câmeras",
    title: "Monitoramento por câmeras na sua casa ou empresa",
    subtitle:
      "Instalação, configuração e suporte da Contato. Assista tudo pelo celular, de onde estiver, com imagens gravadas na nuvem.",
    text: "Nossos planos de monitoramento incluem câmeras de alta definição, aplicativo para celular, gravação em nuvem e visita técnica de instalação feita pela nossa equipe. Ideal para residências, comércios e empresas que precisam de segurança sem complicação.",
    items: [
      {
        name: "Câmera Essencial",
        price: "R$ 49,90 / mês",
        note: "2 câmeras",
        ctaLabel: "Contratar",
        features: [
          "2 câmeras Full HD",
          "Gravação em nuvem por 7 dias",
          "App para celular",
          "Instalação inclusa",
        ],
      },
      {
        name: "Câmera Plus",
        price: "R$ 89,90 / mês",
        note: "4 câmeras",
        badge: "Mais vendido",
        ctaLabel: "Contratar",
        features: [
          "4 câmeras Full HD",
          "Gravação em nuvem por 15 dias",
          "Visão noturna e detecção de movimento",
          "App para celular e suporte 7 dias",
        ],
      },
      {
        name: "Câmera Empresa",
        price: "Sob consulta",
        note: "Projeto sob medida",
        ctaLabel: "Falar com consultor",
        features: [
          "Projeto com quantidade de câmeras sob medida",
          "Gravação em nuvem estendida",
          "Acessos por usuário e relatórios",
          "Manutenção preventiva",
        ],
      },
    ],
  },

  apps: {
    eyebrow: "Contato Aplicativos",
    title: "Entretenimento incluso no seu plano",
    subtitle:
      "Streaming, música e TV ao vivo no mesmo boleto da sua internet, sem cartão de crédito e sem burocracia.",
    text: "Assine os aplicativos que você mais usa direto com a Contato: tudo cai na mesma fatura da sua internet e a ativação é feita na hora pelo nosso atendimento. Assista no celular, na TV ou no computador, quando e onde quiser.",
    items: [
      {
        name: "Aplicativos Premium",
        price: "R$ 24,90/ mês",
        note: "1 aplicativo",
        badge: "Mais vendido",
        ctaLabel: "Contratar",
        tier: "premium",
        features: ["Escolha 1 app do catálogo", "Troque de app a cada mês"],
      },
      {
        name: "Aplicativos Standard",
        price: "R$ 11,90/ mês",
        note: "1 aplicativo",
        ctaLabel: "Contratar",
        tier: "standard",
        features: ["Escolha 1 app do catálogo", "Troque de app a cada mês"],
      },
    ],
    apps: [
      { name: "Deezer", image: "/apps/deezer.svg" },
      { name: "Max", image: "/apps/max.svg" },
      { name: "Looke", image: "/apps/looke.svg" },
      { name: "Paramount+", image: "/apps/paramount-plus.svg" },
      { name: "Contato TV", image: "/apps/contato-tv.svg" },
      { name: "Watch Brasil", image: "/apps/watch-brasil.svg" },
    ],
  },

  blog: {
    title: "Blog Contato",
    subtitle: "Dicas de tecnologia, entretenimento e novidades da sua região.",
    items: [
      {
        title:
          "O Criciúma conquistou mais uma vitória importante na Série B do Campeonato Brasileiro",
        date: "07/07/2026",
        excerpt:
          "O Tigre venceu o Sport por 1 a 0 no Heriberto Hülse, com gol de Waguininho, e somou três pontos fundamentais na competição.",
        body: "O Tigre venceu o Sport por 1 a 0 no Heriberto Hülse, em mais uma noite de casa cheia no Sul catarinense.\nWaguininho marcou no segundo tempo e garantiu três pontos fundamentais na briga pelo acesso.\nQuem é cliente Contato assistiu tudo sem travar: fibra óptica de verdade, com upload alto e baixa latência para transmissões ao vivo.",
        image: "/blog/futebol.jpg",
      },
      {
        title: "Os destaques do Disney+ em junho: confira as estreias que movimentaram o streaming",
        date: "30/06/2026",
        excerpt:
          "Junho foi recheado de novidades para os assinantes do Disney+, com estreias de aventura, animação e séries premiadas.",
        body: "Junho trouxe uma leva forte de estreias no Disney+, entre aventuras, animações e séries premiadas.\nA dica é organizar a maratona por categoria e aproveitar os fins de semana.\nCom os planos de fibra da Contato você assiste em 4K em vários aparelhos ao mesmo tempo, sem fila e sem travar.",
        image: "/blog/streaming.jpg",
      },
      {
        title: "Criciúma vence o São Bernardo e segue firme na Série B",
        date: "29/06/2026",
        excerpt:
          "Gol de Otero, de pênalti, garantiu mais três pontos importantes para o Tigre no Estádio Heriberto Hülse.",
        body: "Gol de Otero, de pênalti, garantiu mais três pontos importantes para o Tigre no Estádio Heriberto Hülse.\nA equipe segue firme na parte de cima da tabela da Série B.\nAcompanhe os próximos jogos com a conexão estável da Contato, do celular à TV.",
        image: "/blog/futebol.jpg",
      },
      {
        title: "Lançamentos da HBO Max em maio de 2026: veja o que chegou ao catálogo",
        date: "27/05/2026",
        excerpt:
          "Confira os filmes e séries que entraram no catálogo da HBO Max e prepare a sua maratona.",
        body: "O catálogo da HBO Max recebeu novos filmes e séries em maio, com destaque para produções originais e clássicos que voltaram ao ar.\nSeparamos os títulos que valem a pena entrar na sua lista.\nTodos os planos de internet da Contato incluem Wi-Fi e velocidade de sobra para streaming em alta definição.",
        image: "/blog/streaming.jpg",
      },
      {
        title: "Jogos para curtir em família: diversão garantida para todas as idades",
        date: "12/04/2026",
        excerpt:
          "Selecionamos jogos online e offline para aproveitar a internet de verdade com quem você ama.",
        body: "Selecionamos jogos online e offline para aproveitar com quem você ama, das crianças aos avós.\nDos clássicos de tabuleiro digitais aos títulos cooperativos online, dá para reunir todo mundo na sala.\nCom o upload simétrico da fibra Contato, jogar online fica sem lag mesmo com a casa toda conectada.",
        image: "/blog/jogos-familia.jpg",
      },
      {
        title: "Lançamentos do Disney+ e HBO Max em março de 2026: prepare sua maratona",
        date: "10/03/2026",
        excerpt:
          "As estreias do mês nas duas plataformas para você assistir com a fibra da Contato.",
        body: "Março movimentou as duas maiores plataformas de streaming com estreias muito aguardadas.\nMontamos um resumo para você não perder nada e já programar a maratona.\nA fibra da Contato entrega estabilidade para assistir em qualquer tela, a qualquer hora.",
        image: "/blog/streaming.jpg",
      },
    ],
  },

  jobs: {
    title: "Trabalhe conosco",
    subtitle:
      "Venha fazer parte de um time catarinense que conecta mais de 30 municípios do Sul de Santa Catarina.",
    items: [
      { role: "Auxiliar de Faxina", location: "Araranguá", url: "" },
      { role: "Monitor de Parque — Contato Fazenda Parque", location: "Araranguá", url: "" },
      { role: "Técnico de Instalação", location: "Tubarão", url: "" },
      { role: "Assistente de Suporte", location: "Araranguá", url: "" },
      { role: "Cabista", location: "Araranguá", url: "" },
      { role: "Assistente de Suporte", location: "Sombrio - SC", url: "" },
      { role: "Técnico de Instalação", location: "Araranguá / Criciúma", url: "" },
    ],
    talentTitle: "Banco de talentos",
    talentText:
      "Caso você não se identifique com as vagas acima, deixe o seu currículo em nosso banco de talentos. Assim que tivermos uma vaga no seu perfil, entraremos em contato.",
    talentCtaLabel: "Cadastrar currículo",
    talentCtaHref: "https://engrenagemrh.empregare.com/pt-br/cadastrar-curriculo",
  },

  stores: {
    title: "Nossas lojas",
    subtitle: "Atendimento presencial em cinco cidades do Sul de Santa Catarina.",
    items: [
      {
        name: "Matriz Araranguá",
        address: "R. Pedro João Pereira, 900",
        district: "Mato Alto, Araranguá - SC",
        zip: "88904-150",
        mapUrl: "",
      },
      {
        name: "Filial Tubarão",
        address: "R. Sen. Richard, 54",
        district: "Centro, Tubarão - SC",
        zip: "88701-220",
        mapUrl: "",
      },
      {
        name: "Filial Criciúma",
        address: "R. Maria Nazario B. da Silva, 51",
        district: "Vila Nova Esperança, Criciúma - SC",
        zip: "88806-035",
        mapUrl: "",
      },
      {
        name: "Filial Sombrio",
        address: "Av. Papa João XXIII, 290",
        district: "Parque das Avenidas, Sombrio - SC",
        zip: "88960-000",
        mapUrl: "",
      },
      {
        name: "Filial Imbituba",
        address: "R. Alcino da Fonseca, 51",
        district: "Paes Leme, Imbituba - SC",
        zip: "88780-000",
        mapUrl: "",
      },
    ],
  },

  about: {
    title: "Sobre nós",
    text: [
      "A Contato Internet é uma empresa genuinamente catarinense e atua no mercado há 29 anos, conta com uma infraestrutura física bem localizada e que atende as mais modernas necessidades tecnológicas do mercado.",
      "Sua sede localiza-se em Araranguá e conta ainda com filial em Tubarão, Criciúma, Sombrio e Imbituba, atendendo por volta de 30 municípios no Sul do Estado.",
      "A Contato Internet busca sempre oferecer as melhores soluções em serviços de telecomunicações, internet e hospedagem de sites, está em contínuo crescimento e evolução, expandindo sua rede constantemente para melhor atender seus clientes, sejam eles pessoas físicas ou corporativas.",
      "Busca continuamente ampliar a sua gama de produtos e serviços, considerando que a qualidade, o melhor atendimento e a ética são os norteadores da empresa e dos que dela fazem parte.",
    ].join("\n\n"),
    image: "",
    missionTitle: "Missão",
    mission:
      "Promover a conectividade das pessoas e organizações, por meio dos melhores serviços de telecomunicações e internet do Sul de Santa Catarina, tendo a qualidade e a busca constante de tecnologia como diferenciais, de forma a facilitar a vida das pessoas e aproximá-las.",
    visionTitle: "Visão",
    vision:
      "Ser reconhecida como a empresa de melhor desempenho no setor de telecomunicações e internet de Santa Catarina.",
    valuesTitle: "Valores",
    values: [
      "Inovação tecnológica",
      "Ética",
      "Agilidade",
      "Qualidade",
      "Flexibilidade",
      "Valorização humana",
      "Treinamento constante / Capacitação",
    ].join("\n"),
  },

  promo: {
    enabled: true,
    badge: "Promoção",
    title: "Fibra de 800 Mega por R$ 169,80",
    text: "Instalação grátis, Wi-Fi incluso e +200 canais de TV. Consulte a disponibilidade na sua rua.",
    image: "",
    ctaLabel: "Quero essa oferta",
    ctaHref: "whatsapp:Olá! Vi a promoção no site e quero contratar.",
    size: "md",
  },

  integrations: {
    huggyWidgetId: "4482",
    huggyCompany: "9833",
    huggyContextId: "4bbd9f48628046754dab2f66c9859912",
    dataCenterUrl: "https://contatocloud.com.br/",
    webmailUrl: "http://mail.contato.net/email/",
  },

  footer: {
    supportNote: "Suporte 7 dias por semana",
    columns: [
      {
        title: "Contato",
        links: [
          { label: "Sobre nós", to: "/sobre" },
          { label: "Blog Contato", to: "/blog" },
          { label: "Trabalhe conosco", to: "/trabalhe-conosco" },
          { label: "Lojas", to: "/lojas" },
        ],
      },
      {
        title: "Clientes",
        links: [
          { label: "Área do cliente", to: "/area-do-cliente" },
          { label: "Segunda via do boleto", to: clientPortalUrl },
          { label: "Pague via PIX", to: "http://pix.contato.net/" },
          { label: "Webmail", to: "http://mail.contato.net/email/" },
        ],
      },
      {
        title: "Serviços",
        links: [
          { label: "Internet", to: "/planos" },
          { label: "Telefonia", to: "/telefonia" },
          { label: "Data Center", to: "https://contatocloud.com.br/" },
          { label: "Câmeras", to: "/cameras" },
          { label: "Aplicativos", to: "/aplicativos" },
          { label: "Hospedagem", to: "/hospedagem" },
        ],
      },
    ],
  },
};

export type SiteContentKey = keyof SiteContent;

export const siteContentQuery = {
  queryKey: ["site_content"],
  queryFn: async (): Promise<Partial<Record<SiteContentKey, unknown>>> => {
    const { data, error } = await supabase.from("site_content").select("key, value");
    if (error || !data) return {};
    const record: Record<string, unknown> = {};
    for (const row of data) record[row.key] = row.value;
    return record as Partial<Record<SiteContentKey, unknown>>;
  },
  staleTime: 30_000,
};

function merge<K extends SiteContentKey>(key: K, raw: unknown): SiteContent[K] {
  const fallback = contentDefaults[key];
  if (raw == null) return fallback;
  if (Array.isArray(fallback)) {
    return (Array.isArray(raw) && raw.length ? raw : fallback) as SiteContent[K];
  }
  if (typeof raw === "object") {
    return { ...(fallback as object), ...(raw as object) } as SiteContent[K];
  }
  return fallback;
}

/** Conteúdo editável de uma seção, com fallback para os valores padrão. */
export function useSiteSection<K extends SiteContentKey>(key: K): SiteContent[K] {
  const { data } = useQuery(siteContentQuery);
  return merge(key, data?.[key]);
}

export function useBrand() {
  return useSiteSection("brand");
}

/**
 * Link do WhatsApp com a origem do visitante anexada à mensagem, para o
 * atendente saber de onde a pessoa veio. `ctx` = ponto do site (ex.: "planos").
 */
export function waLink(phone: string, message: string, ctx?: string) {
  const text = message + attributionRef(ctx);
  return `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(text)}`;
}

/**
 * Mensagem de contratação com o que a pessoa viu no site — nome do plano,
 * preço e promoção — para o atendente já saber o que oferecer.
 */
export function planoWaMsg(
  tipo: "internet" | "telefonia" | "hospedagem",
  d: { nome: string; preco?: string; promo?: string },
) {
  let preco = "";
  if (d.preco?.trim()) {
    const p = d.preco.trim();
    preco = " por " + (/^r\$/i.test(p) ? p : `R$ ${p}/mês`).replace(/\s+/g, " ");
  }
  const promo = d.promo?.trim() ? ` (${d.promo.trim()})` : "";
  return `Olá! Vi no site o plano de ${tipo} ${d.nome}${preco}${promo} e quero contratar.`;
}
